import {
  defaultAcceptFileType,
  defaultMaxFileSizeInMB,
} from "../../../constants";
import { useChatboxStore } from "../../../store";
import type { UploadResponse } from "../../../types";
import type { FormState } from "./type";
import {
  type UploadItem,
  useUploaderAPI,
  checkFileSizeLimit,
} from "@/components/ui/upload";
import type { FormFieldData, FormValue, FormValueType } from "@/model/form";
import { Button } from "@deputy-ai/ui/button/Button";
import type {
  ControllerRenderProps,
  UseFormReturn,
} from "@deputy-ai/ui/form/Form";
import { ProgressBar } from "@deputy-ai/ui/progressBar/ProgressBar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { FiDownload } from "react-icons/fi";
import { HiMiniXMark } from "react-icons/hi2";
import { LuFile } from "react-icons/lu";
import { MdErrorOutline } from "react-icons/md";

interface UploadedFile {
  name?: string;
  objectName?: string;
  url?: string;
  type?: string;
  size?: number;
}

interface UploadFileItem
  extends Omit<UploadItem<UploadResponse>, "promise" | "uid"> {
  objectName?: string;
  type?: string;
  size?: number;
  downloadUrl?: string;
}

export const FormFileInput = ({
  form,
  formField,
  field,
  formState,
  disabled: disabledInput,
}: {
  form: UseFormReturn<Record<string, FormValueType>>;
  formField: FormFieldData;
  field: ControllerRenderProps<Record<string, FormValueType>, string>;
  formState: FormState;
  disabled: boolean;
}) => {
  const {
    use: { options },
  } = useChatboxStore();

  const { upload } = useUploaderAPI<UploadResponse>({
    action: options?.fileUploadUrl ?? "",
    acceptTypes: ["any"],
  });

  const [files, setFiles] = useState<UploadFileItem[]>([]);

  const uploadedFiles = useMemo(
    () => files.filter((file) => file.status === "done"),
    [files]
  );

  useEffect(() => {
    const defaultFiles: UploadedFile[] = formField.value?.valueMetadata?.files;
    if (!defaultFiles) {
      return;
    }
    setFiles(
      defaultFiles.map((f) => ({
        ...f,
        downloadUrl: f.url,
        status: "done",
      }))
    );
  }, [formField, setFiles]);

  useEffect(() => {
    if (uploadedFiles.length === 0) {
      form.resetField(field.name);
      return;
    }
    const formValue: FormValue = {
      label: formField.def.label,
      value: uploadedFiles.length.toString(),
      valueMetadata: {
        files: uploadedFiles.map(
          (f): UploadedFile => ({
            name: f.name ?? f.originFile?.name,
            objectName: f.objectName ?? f.response?.objectName,
            type: f.type ?? f.originFile?.type,
            size: f.size ?? f.originFile?.size,
          })
        ),
      },
    };
    form.setValue(formField.def.label, JSON.stringify(formValue));
  }, [uploadedFiles, form]);

  const isUploading = uploadedFiles.length !== files.length;

  const uploadFile = useCallback(
    async (file: File) => {
      const uploadItem: UploadItem = upload({
        file,
        onProgress: () => {
          setFiles((files) => [...files]);
        },
        onError: () => {
          form.setError(field.name, {
            message: `fail to upload ${file.name}`,
          });
        },
      });
      setFiles([...files, uploadItem]);
      await uploadItem.promise;
      setFiles((files) => [...files]);
    },
    [upload, files]
  );

  const disabled = formState === "submitted" || formState === "submitting";

  return (
    <div className="flex w-full flex-col gap-2">
      {files.map((f, index) => (
        <div
          className="flex items-center gap-4 rounded-xl border-1 p-3"
          key={index}
        >
          {f.status !== "error" && <LuFile className="w-8" />}
          {f.status === "error" && (
            <MdErrorOutline className="w-8 text-red-500" />
          )}
          <div className="flex w-full flex-col overflow-hidden">
            <div className="grow overflow-hidden text-ellipsis">
              {f.name ?? f.originFile?.name}
            </div>
            {f.progress &&
              (f.status === "uploading" || f.status === "error") && (
                <ProgressBar className="my-2 h-1.5" value={f.progress * 100} />
              )}
          </div>
          {f.status === "uploading" && !disabled && !disabledInput && (
            <Button
              icon={<HiMiniXMark />}
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                f.abort?.();
                setFiles((fs) => {
                  fs.splice(index, 1);
                  return [...fs];
                });
              }}
            />
          )}
          {(f.status === "done" || f.status === "error") &&
            !disabled &&
            !disabledInput && (
              <Button
                icon={<AiOutlineDelete />}
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.preventDefault();
                  setFiles((fs) => {
                    fs.splice(index, 1);
                    return [...fs];
                  });
                }}
              />
            )}
          {formState === "submitted" && f.downloadUrl && (
            <a
              className="cursor-pointer rounded p-1 hover:opacity-50"
              href={f.downloadUrl}
              target="_blank"
              rel="noreferrer"
            >
              <FiDownload />
            </a>
          )}
        </div>
      ))}
      {files.length < defaultMaxUploadFileCount &&
        !disabled &&
        !disabledInput && (
          <input
            className={fileInputBtnClassName}
            type="file"
            disabled={disabled || isUploading}
            accept={defaultAcceptFileType}
            onChange={async (e) => {
              const file = e?.target?.files?.[0];
              if (!file) {
                return;
              }
              e.target.value = "";
              if (!checkFileSizeLimit(file, defaultMaxFileSizeInMB)) {
                form.setError(field.name, {
                  message: `file is too large, max file size is ${defaultMaxFileSizeInMB}MB`,
                });
                return;
              }
              await uploadFile(file);
            }}
          />
        )}
    </div>
  );
};

const fileInputBtnClassName =
  "block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer bg-gray-50 focus:outline-none leading-5 grow";

const defaultMaxUploadFileCount = 10;
